import { ApolloError, gql, useQuery, useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useState, useMemo } from "react";
import {
  ItineraryFragmentFragment,
  StopBlock,
  GroupBlock,
  SegmentBlock,
  SegmentData,
  TripperData,
  OperatorRecord,
  OperatorsByEntityIdsQuery,
} from "../types";

const ITINERARY_BY_ID_QUERY = gql`
  query ItineraryById($id: MongoID!) {
    itineraryById(_id: $id) {
      _id
      name {
        en
        fr
        es
      }
      description {
        en
        fr
        es
      }
      isLoop
      travelType
      coverImage {
        _id
        isVideo
        mux {
          assetId
          playbackId
          status
        }
      }
      trailPlace {
        _id
        type
        outline {
          type
          coordinates
        }
      }
      routePath
      routeDistance
      routeDuration
      routeStatus
      organization {
        name {
          en
          fr
          es
        }
        contact {
          address {
            country
          }
        }
      }
      moreInfoLink
      tripperSettings {
        primaryColor
        fontFamily
        segmentPreset
        mapStyleVariant
        map3dObjects
        map3dBuildings
        map3dTrees
        showPointsOfInterest
      }
      segments {
        _id
        label { en fr es }
        distance
        items {
          _id
          blockType
          entityRef { type entityId }
          title { en fr es }
          notes { en fr es }
          overnight
          featuredImageId
          distanceFromPrev
          timeFromPrev
          link { url label { en fr es } }
          tweenType
          tweenLabel { en fr es }
          content { en fr es }
          groupType
          place
          media { type mediaId images src alt { en fr es } }
          name { en fr es }
          description { en fr es }
          items {
            _id
            blockType
            entityRef { type entityId }
            title { en fr es }
            notes { en fr es }
            overnight
            featuredImageId
            distanceFromPrev
            timeFromPrev
            link { url label { en fr es } }
            tweenType
            tweenLabel { en fr es }
            content { en fr es }
            media { type mediaId images src alt { en fr es } }
          }
        }
      }
    }
  }
`;

const OPERATORS_BY_ENTITY_IDS_QUERY = gql`
  query OperatorsByEntityIds($operatorEntityIds: [ID!]!) {
    operatorsByEntityIds(operatorEntityIds: $operatorEntityIds) {
      _entityId
      type
      operationalStatus
      name {
        en
        fr
        es
      }
      description {
        en
        fr
        es
      }
      images {
        _id
      }
      hoursOfOperation {
        mon { start end }
        tue { start end }
        wed { start end }
        thu { start end }
        fri { start end }
        sat { start end }
        sun { start end }
        breaks {
          mon { start end }
          tue { start end }
          wed { start end }
          thu { start end }
          fri { start end }
          sat { start end }
          sun { start end }
        }
      }
      contact {
        phone
        links {
          website
        }
        address {
          line1
          city
          subdivision
          location {
            coordinates
          }
        }
      }
    }
  }
`;

type UseTripperDataResult = {
  data: TripperData;
  isLoading: boolean;
  error?: ApolloError;
};

/** Collect all unique operator entityIds from embedded segments (including inside groups). */
function collectEntityIds(itinerary: ItineraryFragmentFragment | null): string[] {
  if (!itinerary) return [];
  const ids = new Set<string>();

  for (const segment of itinerary.segments) {
    for (const block of segment.items) {
      collectFromBlock(block, ids);
    }
  }
  return Array.from(ids);
}

function collectFromBlock(block: SegmentBlock, ids: Set<string>) {
  if (block.blockType === "STOP") {
    const stop = block as StopBlock;
    if (stop.entityRef?.entityId) {
      ids.add(stop.entityRef.entityId);
    }
  } else if (block.blockType === "GROUP") {
    const group = block as GroupBlock;
    for (const child of group.items) {
      collectFromBlock(child, ids);
    }
  }
}

/**
 * Returns true if a stop's operator is available (either has no entityRef,
 * or the entityRef resolves to an operator in the lookup map).
 */
function isStopAvailable(
  block: SegmentBlock,
  operatorMap: Record<string, NonNullable<OperatorRecord>>,
): boolean {
  if (block.blockType !== "STOP") return true;
  const entityId = (block as StopBlock).entityRef?.entityId;
  // Stops without an entityRef (custom/manual stops) are always available
  if (!entityId) return true;
  return !!operatorMap[entityId];
}

/**
 * Filters out stops whose operators are unavailable (UNPUBLISHED, DELETED,
 * or never approved). Also removes empty groups and segments that result
 * from filtering.
 */
function filterUnavailableStops(
  segments: SegmentData[],
  operatorMap: Record<string, NonNullable<OperatorRecord>>,
): SegmentData[] {
  return segments
    .map(segment => {
      const filteredItems = segment.items
        .map(block => {
          if (block.blockType === "GROUP") {
            const group = block as GroupBlock;
            const filteredGroupItems = group.items.filter(child =>
              isStopAvailable(child, operatorMap),
            );
            // Remove groups with no remaining stops
            if (filteredGroupItems.length === 0) return null;
            return { ...group, items: filteredGroupItems };
          }
          // Filter individual stops
          if (!isStopAvailable(block, operatorMap)) return null;
          return block;
        })
        .filter((item): item is SegmentBlock => item !== null);

      // Remove segments with no remaining items
      if (filteredItems.length === 0) return null;
      return { ...segment, items: filteredItems };
    })
    .filter((seg): seg is SegmentData => seg !== null);
}

export const useTripperData = (itineraryId: string): UseTripperDataResult => {
  const apolloClient = useApolloClient();
  const [operatorByEntityId, setOperatorByEntityId] = useState<
    Record<string, NonNullable<OperatorRecord>>
  >({});

  const itineraryQuery = useQuery<{ itineraryById: ItineraryFragmentFragment | null }>(
    ITINERARY_BY_ID_QUERY,
    { variables: { id: itineraryId } },
  );
  const { startPolling, stopPolling } = itineraryQuery;
  const itinerary = itineraryQuery.data?.itineraryById ?? null;

  const shouldPollRouteStatus = itinerary?.routeStatus === "generating";
  const operatorEntityIds = useMemo(() => collectEntityIds(itinerary), [itinerary]);
  const operatorEntityIdsKey = useMemo(
    () => operatorEntityIds.slice().sort().join(","),
    [operatorEntityIds],
  );

  useEffect(() => {
    if (shouldPollRouteStatus) {
      startPolling?.(3000);
    } else {
      stopPolling?.();
    }
    return () => stopPolling?.();
  }, [shouldPollRouteStatus, startPolling, stopPolling]);

  // Batch-fetch all operators in one query (only APPROVED versions are returned
  // by the public endpoint — matching the pattern used by Operator Widget).
  useEffect(() => {
    if (!operatorEntityIdsKey) {
      setOperatorByEntityId({});
      return;
    }
    const ids = operatorEntityIdsKey.split(",").filter(Boolean);
    let cancelled = false;

    apolloClient
      .query<OperatorsByEntityIdsQuery>({
        query: OPERATORS_BY_ENTITY_IDS_QUERY,
        variables: { operatorEntityIds: ids },
      })
      .then(result => {
        if (cancelled) return;
        const map: Record<string, NonNullable<OperatorRecord>> = {};
        for (const op of result.data?.operatorsByEntityIds ?? []) {
          if (op?._entityId) map[op._entityId] = op;
        }
        setOperatorByEntityId(map);
      })
      .catch(() => {
        if (!cancelled) setOperatorByEntityId({});
      });

    return () => {
      cancelled = true;
    };
  }, [apolloClient, operatorEntityIdsKey]);

  const data = useMemo<TripperData>(() => {
    const rawSegments = itinerary?.segments ?? [];

    // Filter out stops whose operators are unavailable (UNPUBLISHED/DELETED/
    // never approved). The batch query only returns APPROVED operators, so any
    // stop with an entityRef that isn't in the map should be hidden.
    const segments = filterUnavailableStops(rawSegments, operatorByEntityId);

    return {
      itinerary,
      segments,
      operatorByEntityId,
    };
  }, [itinerary, operatorByEntityId]);

  const isLoading = itineraryQuery.loading && !itineraryQuery.data;

  // TODO: Remove this debug hotkey after tripper debugging is complete
  const copyDebugData = useCallback(() => {
    if (isLoading || !itinerary) {
      console.log("[Tripper Debug] No data loaded yet.");
      return;
    }
    const requestedEntityIds = collectEntityIds(itinerary);
    const resolvedEntityIds = Object.keys(data.operatorByEntityId);
    const missingEntityIds = requestedEntityIds.filter(id => !data.operatorByEntityId[id]);

    const debug = JSON.stringify(
      {
        _note: "Tripper viewer debug snapshot — all data used to render this itinerary",
        itineraryId: itinerary._id,
        name: itinerary.name,
        routeStatus: itinerary.routeStatus ?? null,
        routePath: itinerary.routePath ?? null,
        routeDistance: itinerary.routeDistance ?? null,
        routeDuration: itinerary.routeDuration ?? null,
        travelType: itinerary.travelType ?? null,
        isLoop: itinerary.isLoop,
        tripperSettings: itinerary.tripperSettings,
        segmentCount: data.segments.length,
        operators: {
          requested: requestedEntityIds,
          resolved: resolvedEntityIds,
          missing: missingEntityIds,
        },
        segments: data.segments,
        operatorByEntityId: data.operatorByEntityId,
      },
      null,
      2,
    );
    navigator.clipboard.writeText(debug).then(
      () => console.log("[Tripper Debug] Data copied to clipboard."),
      () => console.warn("[Tripper Debug] Clipboard write failed — logging instead:\n", debug),
    );
  }, [data, isLoading, itinerary]);

  // TODO: Remove this debug hotkey after tripper debugging is complete
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Press 'D' key (ignored if typing in an input/textarea)
      if (e.key === "d" || e.key === "D") {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        copyDebugData();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [copyDebugData]);

  // Only surface the error when we have no data to fall back on. Apollo keeps
  // previous `data` across refetch/poll failures, so a transient network blip
  // during polling shouldn't flash the error screen when valid data exists.
  const error = itineraryQuery.error && !itineraryQuery.data ? itineraryQuery.error : undefined;

  return {
    data,
    isLoading,
    error,
  };
};
